/**
 * Product Card — Interactive product cards with Splide carousels, color/size
 * option selection, swatch hover image swap, AJAX add-to-cart, and async
 * source loading (recently-viewed, recommendations, cart-recommendations).
 *
 * ARCHITECTURE
 * ─────────────────────────────────────────────────────────────────────────
 * This module manages product cards across 5 lifecycle states:
 *
 *   1. Boot (page load)         → init()
 *   2. Dialog open (cart drawer) → onDialogOpen(dialog)
 *   3. Cart morph complete       → onCartMorphComplete()
 *   4. Lazy tab reveal           → onTabReveal(splideEl)
 *   5. Shopify editor reload     → init()
 *
 * Each state calls the same shared helpers:
 *   mountNonAsyncCarousels(root) — mounts Splide on non-async carousels
 *   loadAsyncSources(root)       — runs async source loaders
 *   bindCards(root)              — binds click/keydown/submit/swatch handlers
 *
 * FOUNDATION RULES (see CLAUDE.md):
 *   Rule 6: Async carousels (cart-recommendations, recommendations) must
 *           NEVER be mounted empty. Only injectCards mounts them.
 *   Rule 7: Every code path follows the same rules.
 *   Rule 8: Reveal before mount — Splide needs measurable dimensions.
 *   Rule 9: innerHTML over morph for Splide containers.
 *
 * @module product-card
 */
import { CartAddEvent } from '@theme/events';

// ═══════════════════════════════════════════════════════════════════════════
// SELECTORS — Single source of truth. No raw class strings in functions.
// ═══════════════════════════════════════════════════════════════════════════

// Card structure
const CARD_SEL           = '.product-card';
const CARD_TITLE_SEL     = 'a.product-card__title';
const CARD_IMAGE_LINK_SEL = 'a.product-card__image-link';
const CARD_PRICE_SEL     = '.product-card__price';
const IMG_SEL            = '.product-card__image:not(.product-card__image--secondary)';
const IMG_SECONDARY_SEL  = '.product-card__image--secondary';

// ATC panel
const ATC_TRIGGER_SEL    = '.product-card__atc-trigger';
const ATC_PANEL_SEL      = '.product-card__atc-panel';
const ATC_CLOSE_SEL      = '.product-card__atc-close';
const ATC_BUTTON_SEL     = '.product-card__atc';
const ATC_PANEL_OPEN_SEL = '.product-card__atc-panel.is-open';
const FORM_SEL           = '.product-card__form';
const VARIANT_JSON_SEL   = '.product-card__variants-json';
const VARIANT_ID_SEL     = 'input[name="id"]';

// Pills & swatches
const PILL_SEL           = '.product-card__pill';
const SWATCH_SEL         = '.product-card__pill--swatch';
const FAMILY_SWATCH_SEL  = '.product-card__family-swatch';
const SWATCH_MORE_SEL    = '.product-card__swatch-more';
const INFO_SWATCHES_SEL  = '.product-card__info-swatches';
const OPTION_SEL         = '.product-card__option';

// Carousel structure
const SECTION_SEL        = '.product-carousel';
const SPLIDE_EL_SEL      = '.product-carousel-splide';
const SPLIDE_UNINIT_SEL  = '.product-carousel-splide:not(.is-initialized)';
const SPLIDE_LIST_SEL    = '.splide__list';
const SPLIDE_ARROWS_SEL  = '.splide__arrows';

// Async source exclusion — Foundation Rule 6.
// Used in EVERY mount path to prevent mounting empty async carousels.
const ASYNC_SOURCES      = ['cart-recommendations', 'recommendations', 'recently-viewed'];
const NON_ASYNC_NOT      = ASYNC_SOURCES.map(s => `:not([data-source="${s}"])`).join('');
const SPLIDE_SYNC_SEL    = `${SPLIDE_UNINIT_SEL}${NON_ASYNC_NOT}`;

// Containers that hold product cards
const CARD_CONTAINER_SEL = '.product-carousel, .tabbed-collections, .product-grid-container, .cart__content';

// Async container wrapper (hidden until cards load)
const ASYNC_CONTAINER_SEL = '.cart__carousel--async';

// Cart page component (morphed on cart updates — parallel to cart-drawer-component)
const CART_PAGE_SEL       = 'cart-items-component';

// Binding data attributes (prevent double-bind)
const BOUND              = 'data-pc-bound';
const SWATCH_BOUND       = 'data-pc-swatch-bound';

// Section rendering
const CARD_FETCH_SECTION = 'product-card-render';

// Image srcset default widths
const DEFAULT_WIDTHS     = [200, 300, 400, 600, 832];

// Gap token → px map (mirrors --space-gap-* from _variables.scss)
const GAP_MAP = { none: 0, '2xs': 8, xs: 16, sm: 20, md: 24, lg: 28, xl: 32, '2xl': 36 };

// ═══════════════════════════════════════════════════════════════════════════
// ASYNC SOURCE REGISTRY
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Registry for async data sources that populate carousels client-side.
 * Each source is a named loader function that receives a Splide element,
 * fetches product data, and calls injectCards() to populate + mount.
 */
const asyncSources = (() => {
  const registry = new Map();
  return {
    register: (source, loader) => registry.set(source, loader),
    has: (source) => registry.has(source),
    /**
     * Run all registered loaders for uninitialized carousels matching their source.
     * @param {Element} [root=document] — Scope to search within
     */
    async initAll(root = document) {
      for (const [source, loader] of registry) {
        root.querySelectorAll(`${SPLIDE_UNINIT_SEL}[data-source="${source}"]`).forEach((el) =>
          loader(el).catch((err) => console.error(`ProductCard [${source}]:`, err))
        );
      }
    },
  };
})();

// ═══════════════════════════════════════════════════════════════════════════
// SPLIDE CAROUSEL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Resolve a gap token (e.g. "xs") or raw px value to a CSS px string.
 * @param {string} v — Gap token or px value
 * @returns {string} CSS value like "16px"
 */
function resolveGap(v) {
  if (!v || v === 'none') return '0px';
  if (v in GAP_MAP) return `${GAP_MAP[v]}px`;
  return v.includes?.('px') ? v : `${v}px`;
}

/**
 * Mount a Splide carousel on the given element.
 *
 * - Destroys any existing instance first (safe for re-mount after innerHTML).
 * - Stores the instance on el._splide for later cleanup.
 * - Stores the resize handler on el._resizeHandler so it can be removed.
 * - Skips if element already has .is-initialized (idempotent).
 *
 * Foundation Rule 8: The element MUST be visible (not inside a closed dialog
 * or hidden container) when this is called. Splide needs measurable dimensions.
 *
 * @param {HTMLElement} el — The .product-carousel-splide element
 */
function mountSplide(el) {
  if (typeof Splide === 'undefined' || el.classList.contains('is-initialized')) return;

  // Rule 8: Never mount inside a closed <dialog> — Splide measures offsetWidth: 0
  // on display:none elements, producing a 0-width track that persists after open.
  // onDialogOpen handles mounting when the dialog actually becomes visible.
  const parentDialog = el.closest('dialog');
  if (parentDialog && !parentDialog.open) return;

  // Destroy previous instance if re-mounting (e.g. after cart innerHTML replacement)
  if (el._splide) {
    if (el._resizeHandler) window.removeEventListener('resize', el._resizeHandler);
    try { el._splide.destroy(); } catch { /* already destroyed */ }
    el._splide = null;
    el._resizeHandler = null;
  }

  const d = el.dataset;
  const gapPx = resolveGap(d.gap);
  const mobileGapVal = d.gap in GAP_MAP
    ? Math.max(GAP_MAP[d.gap] - 4, 8)
    : Math.max(parseInt(d.gap) - 4, 8);
  const perPage = parseInt(d.desktopColumns) || 4;

  const splide = new Splide(el, {
    type: 'slide',
    perPage,
    perMove: 1,
    gap: gapPx,
    arrows: d.arrows === 'true',
    pagination: d.dots === 'true',
    drag: true,
    speed: 400,
    padding: { right: `${parseInt(d.desktopPeek) || 0}%` },
    breakpoints: {
      768: {
        perPage: parseInt(d.mobileColumns) || 2,
        gap: `${mobileGapVal}px`,
        padding: { right: `${parseInt(d.mobilePeek) || 0}%` },
      },
    },
  }).mount();

  // Store instance for cleanup
  el._splide = splide;

  // Auto-hide arrows when slide count <= perPage
  const arrowsEl = el.querySelector(SPLIDE_ARROWS_SEL);
  if (arrowsEl) {
    const updateArrows = () => {
      arrowsEl.style.display = splide.length <= splide.options.perPage ? 'none' : '';
    };
    updateArrows();
    el._resizeHandler = updateArrows;
    window.addEventListener('resize', updateArrows);
  }

  el.classList.add('is-initialized');
}

/**
 * Mount all non-async Splide carousels within root.
 * Async carousels are excluded — Foundation Rule 6.
 * @param {Element} [root=document]
 */
function mountNonAsyncCarousels(root = document) {
  root.querySelectorAll(SPLIDE_SYNC_SEL).forEach((el) => {
    if (asyncSources.has(el.dataset.source)) return;
    mountSplide(el);
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/** Format cents as currency string. */
const formatMoney = (cents) =>
  (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  });

/** Get the ATC button label based on availability. */
const getATCLabel = (available) => {
  const s = window.theme?.strings;
  return available ? (s?.add_to_cart ?? 'Add to Cart') : (s?.sold_out ?? 'Sold Out');
};

/**
 * Get or parse the variant JSON for a card.
 * Cached on card._variants — GC'd when card is removed from DOM.
 * @param {HTMLElement} card
 * @returns {Array|null}
 */
function getCardVariants(card) {
  if (card._variants) return card._variants;
  const el = card.querySelector(VARIANT_JSON_SEL);
  if (!el) return null;
  try { card._variants = JSON.parse(el.textContent); } catch { return null; }
  return card._variants;
}

/** Find a variant matching the selected options. */
function findVariant(variants, selected) {
  return variants.find((v) => Object.keys(selected).every((k) => v[k] === selected[k]));
}

/** Collect active pill selections from a card. */
function getActiveSelections(card) {
  const selected = {};
  card.querySelectorAll(`${PILL_SEL}.active`).forEach((p) => {
    selected[`option${p.dataset.optionPosition}`] = p.dataset.optionValue;
  });
  return selected;
}

// ═══════════════════════════════════════════════════════════════════════════
// PRICE
// ═══════════════════════════════════════════════════════════════════════════

/** Update the price display on a card after variant change. */
function updateCardPrice(card, variant) {
  const wrapper = card.querySelector(CARD_PRICE_SEL);
  if (!wrapper || !variant) return;

  const price = formatMoney(variant.price);
  const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;

  wrapper.innerHTML = hasCompare
    ? `<span class="price-group--compare"><span class="visually-hidden">Regular price\u00a0</span><span class="compare-at-price">${formatMoney(variant.compare_at_price)}</span></span><span class="price-group--sale"><span class="visually-hidden">Sale price\u00a0</span><span class="price">${price}</span></span>`
    : `<span class="price">${price}</span>`;
}

// ═══════════════════════════════════════════════════════════════════════════
// IMAGE SWAP
// ═══════════════════════════════════════════════════════════════════════════

/** Build a responsive srcset from a Shopify image URL and width array. */
function buildSrcset(url, widths) {
  return widths.map((w) => `${url.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`).join(', ');
}

/**
 * Swap the primary card image to a new source.
 * Stores the original src/srcset for later restoration.
 */
function swapCardImage(img, src) {
  if (!img || !src) return;
  if (!img.dataset.originalSrc) {
    img.dataset.originalSrcset = img.getAttribute('srcset') || '';
    img.dataset.originalSrc = img.src;
  }
  const widths = img.dataset.originalSrcset.match(/\d+w/g)?.map((w) => parseInt(w)) || DEFAULT_WIDTHS;
  img.setAttribute('srcset', buildSrcset(src, widths));
  img.src = src;
}

/** Restore the primary card image to its original source. */
function restoreCardImage(img) {
  if (!img?.dataset.originalSrc) return;
  img.setAttribute('srcset', img.dataset.originalSrcset);
  img.src = img.dataset.originalSrc;
}

// ═══════════════════════════════════════════════════════════════════════════
// ATC PANEL
// ═══════════════════════════════════════════════════════════════════════════

/** Open the ATC overlay panel on a product card. */
function openATCPanel(media) {
  if (!media) return;
  const panel = media.querySelector(ATC_PANEL_SEL);
  if (!panel) return;
  panel.classList.add('is-open');
  panel.setAttribute('aria-hidden', 'false');
  media.querySelector(ATC_TRIGGER_SEL)?.classList.add('is-hidden');
  panel.querySelector(ATC_CLOSE_SEL)?.focus();
}

/** Close the ATC overlay panel. */
function closeATCPanel(panel) {
  if (!panel) return;
  panel.classList.remove('is-open');
  panel.setAttribute('aria-hidden', 'true');
  const trigger = panel.closest('.product-card__media')?.querySelector(ATC_TRIGGER_SEL);
  trigger?.classList.remove('is-hidden');
  trigger?.focus();
}

// ═══════════════════════════════════════════════════════════════════════════
// PILL & SWATCH INTERACTION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Handle click on a size/option pill (non-family).
 * Toggles active state, updates variant ID, price, and ATC button.
 */
function handlePillClick(pill) {
  const card = pill.closest(CARD_SEL);
  if (!card) return;

  // Family swatches delegate to handleInfoSwatchClick
  if (pill.dataset.siblingUrl) {
    handleInfoSwatchClick(pill);
    return;
  }

  const { optionPosition: pos, optionValue: val } = pill.dataset;

  // Toggle active on all pills AND swatches with the same option position
  card.querySelectorAll(`${PILL_SEL}[data-option-position="${pos}"], ${SWATCH_SEL}[data-option-position="${pos}"]`).forEach((p) => {
    const match = p.dataset.optionValue === val;
    p.classList.toggle('active', match);
    if (p.hasAttribute('aria-checked')) p.setAttribute('aria-checked', String(match));
  });

  const variants = getCardVariants(card);
  if (!variants) return;

  const variant = findVariant(variants, getActiveSelections(card));
  if (!variant) return;

  const idInput = card.querySelector(VARIANT_ID_SEL);
  const atcBtn = card.querySelector(ATC_BUTTON_SEL);
  if (idInput) idInput.value = variant.id;
  if (atcBtn) {
    atcBtn.disabled = !variant.available;
    atcBtn.textContent = getATCLabel(variant.available);
  }
  updateCardPrice(card, variant);

  // Swap card image to match variant's featured image
  if (pill.classList.contains(SWATCH_SEL.slice(1))) {
    const src = pill.dataset.variantImage || variant.featured_image;
    if (src) swapCardImage(card.querySelector(IMG_SEL), src);
  }
}

/**
 * Handle click on a product family swatch (color swatch that links to a sibling product).
 * In the info area (below price): navigates to sibling.
 * In the ATC panel: swaps card data via Section Rendering API.
 */
function handleInfoSwatchClick(pill) {
  const card = pill.closest(CARD_SEL);
  if (!card) return;

  const siblingUrl = pill.dataset.siblingUrl;
  const isInPanel = !!pill.closest(ATC_PANEL_SEL);

  // Toggle active state on ALL matching swatches (info + ATC panel stay in sync)
  card.querySelectorAll(`${FAMILY_SWATCH_SEL}, ${SWATCH_SEL}[data-sibling-url]`).forEach((s) => {
    const match = s.dataset.siblingUrl === siblingUrl;
    s.classList.toggle('active', match);
    if (s.hasAttribute('aria-current')) s.setAttribute('aria-current', String(match));
  });

  // Swap the primary card image
  const src = pill.dataset.variantImage;
  if (src) swapCardImage(card.querySelector(IMG_SEL), src);

  // Update the card's main link URLs
  if (siblingUrl) {
    card.querySelectorAll(`${CARD_IMAGE_LINK_SEL}, ${CARD_TITLE_SEL}`).forEach((a) => {
      a.href = siblingUrl;
    });
    card.dataset.originalUrl = siblingUrl;
  }

  // Info area: navigate to sibling product
  if (!isInPanel && siblingUrl) {
    window.location.href = siblingUrl;
    return;
  }

  // ATC panel: fetch sibling data and update the card in-place
  if (isInPanel && siblingUrl) {
    updateCardForSibling(card, siblingUrl);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// SIBLING PRODUCT FETCH (ATC panel color swatch)
// ═══════════════════════════════════════════════════════════════════════════

/** AbortController map — prevents race conditions on rapid swatch clicks. */
const siblingAbort = new WeakMap();

/**
 * Fetch sibling product data via Section Rendering API and update the card's
 * form, variants JSON, title, price, size pills, and images.
 *
 * Uses AbortController: if user clicks another swatch before this completes,
 * the in-flight request is aborted. Last click wins.
 *
 * @param {HTMLElement} card — The .product-card element
 * @param {string} siblingUrl — URL of the sibling product (e.g. /products/handle)
 */
async function updateCardForSibling(card, siblingUrl) {
  const handle = siblingUrl.replace(/^\/products\//, '').split('?')[0];
  if (!handle) return;

  // Abort any in-flight fetch for this card
  siblingAbort.get(card)?.abort();
  const controller = new AbortController();
  siblingAbort.set(card, controller);

  try {
    const resp = await fetch(`${siblingUrl}?sections=${CARD_FETCH_SECTION}`, { signal: controller.signal });
    if (!resp.ok) return;
    const json = await resp.json();
    const html = json[CARD_FETCH_SECTION];
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newCard = doc.querySelector(CARD_SEL);
    if (!newCard) return;

    // Update variant ID
    const newId = newCard.querySelector(VARIANT_ID_SEL);
    const curId = card.querySelector(VARIANT_ID_SEL);
    if (newId && curId) curId.value = newId.value;

    // Update variants JSON (invalidate cache)
    const newJson = newCard.querySelector(VARIANT_JSON_SEL);
    const curJson = card.querySelector(VARIANT_JSON_SEL);
    if (newJson && curJson) {
      curJson.textContent = newJson.textContent;
      delete card._variants; // Invalidate cached variants
    }

    // Update title
    const newTitle = newCard.querySelector(CARD_TITLE_SEL);
    const curTitle = card.querySelector(CARD_TITLE_SEL);
    if (newTitle && curTitle) {
      curTitle.textContent = newTitle.textContent;
      curTitle.href = siblingUrl;
    }

    // Update price
    const newPrice = newCard.querySelector(CARD_PRICE_SEL);
    const curPrice = card.querySelector(CARD_PRICE_SEL);
    if (newPrice && curPrice) curPrice.innerHTML = newPrice.innerHTML;

    // Update size pills (innerHTML replacement — needs swatch rebind)
    const newSizes = newCard.querySelector(`${OPTION_SEL}:not(.product-card__option--swatches)`);
    const curSizes = card.querySelector(`${ATC_PANEL_SEL} ${OPTION_SEL}:not(.product-card__option--swatches)`);
    if (newSizes && curSizes) curSizes.innerHTML = newSizes.innerHTML;

    // Update secondary (hover) image
    const newSecondary = newCard.querySelector(IMG_SECONDARY_SEL);
    const curSecondary = card.querySelector(IMG_SECONDARY_SEL);
    if (newSecondary && curSecondary) {
      curSecondary.src = newSecondary.src;
      curSecondary.setAttribute('srcset', newSecondary.getAttribute('srcset') || '');
    } else if (!newSecondary && curSecondary) {
      curSecondary.style.display = 'none';
    } else if (newSecondary && !curSecondary) {
      const primaryImg = card.querySelector(IMG_SEL);
      if (primaryImg) primaryImg.insertAdjacentElement('afterend', newSecondary);
    }

    // Update original image data so mouseleave restores to sibling's image
    const primaryImg = card.querySelector(IMG_SEL);
    if (primaryImg) {
      primaryImg.dataset.originalSrc = primaryImg.src;
      primaryImg.dataset.originalSrcset = primaryImg.getAttribute('srcset') || '';
    }

    // Update product handle
    card.dataset.productHandle = handle;

    // Re-bind swatch hover on new pill elements (click uses delegation — no rebind needed)
    bindSwatches(card);
  } catch (err) {
    if (err.name !== 'AbortError') console.warn('ProductCard: sibling fetch failed', err);
  } finally {
    siblingAbort.delete(card);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// DELEGATED EVENT HANDLERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Delegated click handler for all product card interactions.
 * Registered once per block/container via bindBlock().
 */
function handleBlockClick(e) {
  const t = e.target;

  // ATC trigger → open panel
  const trigger = t.closest(ATC_TRIGGER_SEL);
  if (trigger) return openATCPanel(trigger.closest('.product-card__media'));

  // Close button → close panel
  const close = t.closest(ATC_CLOSE_SEL);
  if (close) return closeATCPanel(close.closest(ATC_PANEL_SEL));

  // +N overflow button → expand swatches
  const moreBtn = t.closest(SWATCH_MORE_SEL);
  if (moreBtn) {
    const container = moreBtn.closest(INFO_SWATCHES_SEL) || moreBtn.closest(OPTION_SEL);
    if (container) container.classList.add('is-expanded');
    return;
  }

  // Pill or swatch click
  const pill = t.closest(PILL_SEL) || t.closest(SWATCH_SEL) || t.closest(FAMILY_SWATCH_SEL);
  if (pill) {
    if (pill.dataset.siblingUrl) {
      e.preventDefault(); // Prevent <a> navigation — JS handles it
      return handleInfoSwatchClick(pill);
    }
    return handlePillClick(pill);
  }
}

/** Delegated keydown handler for keyboard accessibility. */
function handleBlockKeydown(e) {
  if (e.key === 'Escape') {
    const panel = e.target.closest(ATC_PANEL_SEL);
    if (panel?.classList.contains('is-open')) return closeATCPanel(panel);
  }
  if (e.key === 'Enter' || e.key === ' ') {
    const swatch = e.target.closest(SWATCH_SEL) || e.target.closest(FAMILY_SWATCH_SEL);
    if (swatch) {
      e.preventDefault();
      return swatch.dataset.siblingUrl
        ? handleInfoSwatchClick(swatch)
        : handlePillClick(swatch);
    }
  }
}

/**
 * Delegated form submit handler for AJAX add-to-cart.
 * Includes double-submit guard (form._submitting flag).
 */
function handleFormSubmit(e) {
  const form = e.target;
  if (!form.classList?.contains(FORM_SEL.slice(1))) return;
  e.preventDefault();
  e.stopPropagation();

  // Double-submit guard
  if (form._submitting) return;
  form._submitting = true;

  const item = { id: parseInt(new FormData(form).get('id')), quantity: 1 };
  const atcBtn = form.querySelector(ATC_BUTTON_SEL);

  if (atcBtn) { atcBtn.disabled = true; atcBtn.textContent = 'Adding...'; }

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [item] }),
  })
    .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
    .then(async () => {
      // Fetch actual cart count for accurate bubble update
      const cartRes = await fetch('/cart.js');
      const cart = cartRes.ok ? await cartRes.json() : null;
      document.dispatchEvent(new CartAddEvent(null, null, {
        itemCount: cart?.item_count ?? 0,
        source: 'product-card',
      }));
      if (atcBtn) {
        atcBtn.textContent = 'Added!';
        setTimeout(() => { atcBtn.disabled = false; atcBtn.textContent = getATCLabel(true); }, 1500);
      }
      closeATCPanel(form.closest(ATC_PANEL_SEL));
    })
    .catch((err) => {
      console.error('ProductCard: cart add error', err);
      if (atcBtn) { atcBtn.disabled = false; atcBtn.textContent = getATCLabel(true); }
    })
    .finally(() => { form._submitting = false; });
}

// ═══════════════════════════════════════════════════════════════════════════
// SWATCH HOVER — Image swap on mouseenter/leave
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Swap card image to the hovered swatch's variant image.
 * Skipped when ATC panel is open (panel swatch clicks handle images instead).
 * Uses cached _card and _img references from bindSwatches for performance.
 */
function handleSwatchEnter() {
  if (!this.dataset.variantImage) return;
  const card = this._card || this.closest(CARD_SEL);
  if (!card) return;
  if (card.querySelector(ATC_PANEL_OPEN_SEL)) return;
  const img = this._img || card.querySelector(IMG_SEL);
  swapCardImage(img, this.dataset.variantImage);
}

/** Restore card image on swatch mouseleave. */
function handleSwatchLeave() {
  const card = this._card || this.closest(CARD_SEL);
  if (!card) return;
  if (card.querySelector(ATC_PANEL_OPEN_SEL)) return;
  const active = card.querySelector(`${FAMILY_SWATCH_SEL}.active[data-variant-image], ${SWATCH_SEL}.active[data-variant-image]`);
  const img = this._img || card.querySelector(IMG_SEL);
  if (active && active !== this) swapCardImage(img, active.dataset.variantImage);
  else if (!active) restoreCardImage(img);
}

// ═══════════════════════════════════════════════════════════════════════════
// BINDING — Attach event handlers to cards
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bind swatch hover handlers within a root element.
 * Caches card + image references on each swatch for fast hover lookups.
 * @param {Element} root — Card or container to search within
 */
function bindSwatches(root) {
  const sel = `${FAMILY_SWATCH_SEL}:not([${SWATCH_BOUND}]), ${SWATCH_SEL}:not([${SWATCH_BOUND}])`;
  root.querySelectorAll(sel).forEach((s) => {
    s.setAttribute(SWATCH_BOUND, '');
    s._card = s.closest(CARD_SEL);
    s._img = s._card?.querySelector(IMG_SEL);
    s.addEventListener('mouseenter', handleSwatchEnter);
    s.addEventListener('mouseleave', handleSwatchLeave);
  });
}

/**
 * Bind delegated click/keydown/submit handlers on a block element.
 * Uses data attribute guard to prevent double-binding.
 * @param {Element} block — Container or card element
 */
function bindBlock(block) {
  if (block.hasAttribute(BOUND)) return;
  block.setAttribute(BOUND, '');
  block.addEventListener('click', handleBlockClick);
  block.addEventListener('keydown', handleBlockKeydown);
  block.addEventListener('submit', handleFormSubmit);
  bindSwatches(block);
}

/**
 * Bind all product cards within a scoped root.
 * Binds both containers (for event delegation) and individual cards (for direct access).
 * @param {Element} [root=document] — Scope to bind within
 */
function bindCards(root = document) {
  root.querySelectorAll(CARD_CONTAINER_SEL).forEach(bindBlock);
  root.querySelectorAll(CARD_SEL).forEach((card) => {
    bindBlock(card);
    bindSwatches(card);
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// ASYNC CARD INJECTION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Inject product card HTML into a Splide carousel's slide list, then mount.
 *
 * Foundation Rule 8: Reveals the async container BEFORE mounting Splide.
 * Foundation Rule 6: This is the ONLY function that mounts async carousels.
 *
 * @param {HTMLElement} splideEl — The .product-carousel-splide element
 * @param {string[]} htmlArray — Array of card HTML strings
 */
function injectCards(splideEl, htmlArray) {
  const list = splideEl.querySelector(SPLIDE_LIST_SEL);
  if (!list) return;

  // Guard: don't inject into an already-initialized carousel (prevents double-injection
  // when both onDialogOpen and onCartMorphComplete trigger asyncSources.initAll)
  if (splideEl.classList.contains('is-initialized')) return;

  // Guard: don't inject if list already has slides (previous inject still pending mount)
  if (list.children.length > 0) return;

  const frag = document.createDocumentFragment();
  for (const html of htmlArray) {
    if (!html?.trim()) continue;
    const li = document.createElement('li');
    li.className = 'splide__slide';
    li.innerHTML = html;
    frag.appendChild(li);
  }
  list.appendChild(frag);

  // No cards injected — keep container hidden
  if (!list.children.length) {
    const asyncContainer = splideEl.closest(ASYNC_CONTAINER_SEL);
    if (asyncContainer) asyncContainer.hidden = true;
    return;
  }

  // Reveal BEFORE mount — Splide needs measurable dimensions (Rule 8)
  const asyncContainer = splideEl.closest(ASYNC_CONTAINER_SEL);
  if (asyncContainer) asyncContainer.hidden = false;

  mountSplide(splideEl);

  // Reveal section wrapper and bind cards
  const section = splideEl.closest(SECTION_SEL);
  if (section) {
    bindBlock(section);
    bindSwatches(section);
    section.classList.remove('product-carousel--hidden');
  }
}

/**
 * Fetch product card HTML for an array of product handles.
 * Uses the product-card-render section for consistent markup.
 * @param {string[]} handles — Product handles
 * @returns {Promise<string[]>} Array of card HTML strings
 */
async function fetchCardsByHandle(handles) {
  const results = await Promise.all(
    handles.map((h) =>
      fetch(`/products/${h}?sections=${CARD_FETCH_SECTION}`)
        .then((r) => r.ok ? r.json() : null)
        .catch((err) => { console.warn('ProductCard: card fetch failed', h, err); return null; })
    )
  );
  return results.map((json) => {
    const html = json?.[CARD_FETCH_SECTION];
    if (!html) return null;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const wrapper = temp.querySelector(`#shopify-section-${CARD_FETCH_SECTION}`);
    return wrapper ? wrapper.innerHTML.trim() : html;
  }).filter(Boolean);
}

// ═══════════════════════════════════════════════════════════════════════════
// BUILT-IN ASYNC SOURCES
// ═══════════════════════════════════════════════════════════════════════════

/** Recently viewed products — reads handles from localStorage. */
asyncSources.register('recently-viewed', async (splideEl) => {
  let handles;
  try {
    const { RecentlyViewed } = await import('@theme/recently-viewed-products');
    handles = RecentlyViewed.getHandles();
  } catch {
    const raw = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
    handles = raw.filter((e) => typeof e === 'object' && e.handle).map((e) => e.handle);
  }
  if (!handles.length) return;
  injectCards(splideEl, await fetchCardsByHandle(handles));
});

/** Product recommendations — fetches from Shopify API based on product ID. */
asyncSources.register('recommendations', async (splideEl) => {
  const productId = splideEl.dataset.productId;
  if (!productId) return;
  const root = window.Shopify?.routes?.root || '/';
  const res = await fetch(`${root}recommendations/products.json?product_id=${productId}&limit=16`);
  if (!res.ok) return;
  const { products } = await res.json();
  if (!products?.length) return;
  injectCards(splideEl, await fetchCardsByHandle(products.map((p) => p.handle)));
});

/** Cart recommendations — fetches recs based on last-added cart item, filters out items already in cart. */
asyncSources.register('cart-recommendations', async (splideEl) => {
  const root = window.Shopify?.routes?.root || '/';
  const cartRes = await fetch(`${root}cart.js`);
  if (!cartRes.ok) return;
  const cart = await cartRes.json();
  if (!cart.items?.length) return;

  const productId = cart.items[0].product_id;
  const res = await fetch(`${root}recommendations/products.json?product_id=${productId}&limit=12`);
  if (!res.ok) return;
  const { products } = await res.json();
  if (!products?.length) return;

  const cartProductIds = new Set(cart.items.map((item) => item.product_id));
  const filtered = products.filter((p) => !cartProductIds.has(p.id));
  if (!filtered.length) return;

  injectCards(splideEl, await fetchCardsByHandle(filtered.map((p) => p.handle)));
});

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export { asyncSources, injectCards, fetchCardsByHandle };

// ═══════════════════════════════════════════════════════════════════════════
// LIFECYCLE STATE HANDLERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * STATE 1 & 5: Boot / Shopify editor reload.
 * Mounts all visible carousels, binds all cards, starts async sources.
 */
export function init() {
  if (!window.Shopify?.designMode) asyncSources.initAll();
  mountNonAsyncCarousels();
  bindCards();
}

/**
 * STATE 2: Dialog opened (cart drawer, quickview, etc.).
 * Mounts carousels inside the dialog (they couldn't init while closed).
 * Scoped to the dialog — doesn't touch the rest of the page.
 * @param {HTMLElement} dialog
 */
function onDialogOpen(dialog) {
  dialog.querySelectorAll(SPLIDE_SYNC_SEL).forEach(mountSplide);

  // Mount async carousels whose slides were injected while dialog was closed.
  // Race condition: init() → asyncSources.initAll() → injectCards() appends slides,
  // but mountSplide() bails at the parentDialog.open check. Now the dialog is open,
  // so we can mount any carousel that has slides but never got a Splide instance.
  ASYNC_SOURCES.forEach(source => {
    dialog.querySelectorAll(`${SPLIDE_UNINIT_SEL}[data-source="${source}"]`).forEach(el => {
      const list = el.querySelector(SPLIDE_LIST_SEL);
      if (list && list.children.length > 0) mountSplide(el);
    });
  });

  bindCards(dialog);
  asyncSources.initAll(dialog);
}

/**
 * STATE 3: Cart morph complete.
 * Called after cart-items-component replaces its innerHTML.
 * Resets stale async carousels, mounts fresh ones, rebinds cards.
 */
function onCartMorphComplete() {
  const drawer = document.querySelector('cart-drawer-component');

  // Reset stale async carousels (already initialized with old data)
  document.querySelectorAll(`${SPLIDE_EL_SEL}.is-initialized[data-source="cart-recommendations"]`).forEach((el) => {
    // Destroy old Splide instance cleanly
    if (el._resizeHandler) window.removeEventListener('resize', el._resizeHandler);
    if (el._splide) try { el._splide.destroy(); } catch { /* ok */ }
    el._splide = null;
    el._resizeHandler = null;
    el.classList.remove('is-initialized');
    const list = el.querySelector(SPLIDE_LIST_SEL);
    if (list) list.innerHTML = '';
    const container = el.closest(ASYNC_CONTAINER_SEL);
    if (container) container.hidden = true;
  });

  // Mount non-async carousels inside the cart drawer
  if (drawer) {
    drawer.querySelectorAll(SPLIDE_SYNC_SEL).forEach(mountSplide);
  }

  // Mount non-async carousels on the cart page (cart-items-component is morphed too)
  const cartPage = document.querySelector(CART_PAGE_SEL);
  if (cartPage) {
    cartPage.querySelectorAll(SPLIDE_SYNC_SEL).forEach(mountSplide);
  }

  // Re-observe new dialogs (morph may have created new ones)
  observeDialogs();

  // Bind cards scoped to their respective containers
  if (drawer) bindCards(drawer);
  if (cartPage) bindCards(cartPage);

  // Populate async carousels (cart-recommendations fetches recs then mounts)
  asyncSources.initAll();
}

/**
 * STATE 4: Lazy tab revealed in tabbed collections.
 * Mounts a single Splide instance and binds its parent container.
 * @param {HTMLElement} splideEl
 */
function onTabReveal(splideEl) {
  mountSplide(splideEl);
  bindBlock(splideEl.closest(SECTION_SEL) || splideEl.closest('.tabbed-collections'));
}

// ═══════════════════════════════════════════════════════════════════════════
// OBSERVERS & EVENT LISTENERS
// ═══════════════════════════════════════════════════════════════════════════

// ── Dialog open observer ──
// Watches the `open` attribute on all <dialog> elements.
// Fires onDialogOpen when a dialog becomes visible.
const dialogObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.attributeName !== 'open' || !(mutation.target instanceof HTMLElement)) continue;
    const dialog = mutation.target;
    if (!dialog.hasAttribute('open')) continue;
    onDialogOpen(dialog);
  }
});

/** Observe all unobserved dialogs for open/close changes. */
function observeDialogs() {
  document.querySelectorAll('dialog:not([data-pc-observed])').forEach((dialog) => {
    dialog.setAttribute('data-pc-observed', '');
    dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  });
}

// ── Cart morph complete ──
// Dispatched by cart-items-component after innerHTML replacement finishes.
document.addEventListener('cart:morph-complete', onCartMorphComplete);

// ── Lazy tab reveal ──
// Dispatched by tabbed-collections when a hidden tab panel is activated.
document.addEventListener('splide:lazy-init', (e) => {
  const el = e.target;
  if (el?.matches?.(SPLIDE_UNINIT_SEL)) onTabReveal(el);
});

// ── Shopify editor ──
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', init);
}

// ═══════════════════════════════════════════════════════════════════════════
// BOOT
// ═══════════════════════════════════════════════════════════════════════════

init();
observeDialogs();
